import { useEffect, useId, useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { FileText, Upload, X, Copy, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const WORKOUT_METRICS = [
  { value: "reps", label: "Reps" },
  { value: "duration", label: "Duration" },
  { value: "distance", label: "Distance" },
  { value: "count", label: "Count" },
];

export const DIFFICULTIES = [
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
];

const REST_OPTIONS = [
  10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 130, 140, 150, 160, 170, 180,
];

export function makeDefaultSet(index = 0) {
  return {
    setNumber: index + 1,
    reps: "10",
    restSeconds: "none",
    weightKg: "",
    durationMin: "",
    distanceKm: "",
    count: "10",
  };
}

export function makeDefaultWorkoutFields() {
  return {
    workoutType: "",
    workoutMetric: "reps",
    difficulty: "beginner",
    sets: 1,
    setDetails: [makeDefaultSet(0)],
    attachments: [],
    youtubeLink: "",
    instructions: "",
  };
}

export function normalizeWorkoutFields(value = {}) {
  const sets = Number(value.sets || 1);
  const current = Array.isArray(value.setDetails) ? value.setDetails : [];
  const setDetails = Array.from({ length: sets }, (_, index) => ({
    ...makeDefaultSet(index),
    ...(current[index] || {}),
    setNumber: index + 1,
  }));

  let workoutMetric = value.workoutMetric;
  let workoutType = value.workoutType;
  if (
    !workoutMetric &&
    ["reps", "duration", "distance", "count"].includes(value.workoutType)
  ) {
    workoutMetric = value.workoutType;
    workoutType = "";
  }

  return {
    ...makeDefaultWorkoutFields(),
    ...value,
    workoutMetric: workoutMetric || "reps",
    workoutType: workoutType || "",
    sets,
    setDetails,
    attachments: Array.isArray(value.attachments) ? value.attachments : [],
  };
}

function formatRest(seconds) {
  if (seconds < 60) return `${seconds} sec`;
  if (seconds === 60) return "60 sec";
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest ? `${minutes} min ${rest} sec` : `${minutes} min`;
}

export default function DetailedWorkoutFields({
  value,
  onChange,
  disabled = false,
  showTitle = false,
  titleLabel = "Name of the Workout",
}) {
  const uploadId = useId();
  const data = normalizeWorkoutFields(value);
  const [previewDialog, setPreviewDialog] = useState({
    open: false,
    src: "",
    name: "",
  });

  const isImage = (file) => {
    if (file.type && file.type.startsWith("image/")) return true;
    const url = file.url || "";
    return /\.(jpg|jpeg|png|webp|gif|svg)/i.test(url);
  };

  useEffect(() => {
    if ((value?.setDetails || []).length !== data.sets) {
      onChange(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.sets]);

  const update = (patch) => onChange({ ...data, ...patch });

  const updateSet = (index, patch) => {
    const next = data.setDetails.map((set, setIndex) =>
      setIndex === index ? { ...set, ...patch } : set,
    );
    update({ setDetails: next });
  };

  const duplicateSet = (index) => {
    const setToDuplicate = data.setDetails[index];
    const nextDetails = [...data.setDetails];
    nextDetails.splice(index + 1, 0, {
      ...setToDuplicate,
      setNumber: setToDuplicate.setNumber + 1,
    });
    const updatedDetails = nextDetails.map((set, sIdx) => ({
      ...set,
      setNumber: sIdx + 1,
    }));
    update({
      sets: data.sets + 1,
      setDetails: updatedDetails,
    });
  };

  const deleteSet = (index) => {
    if (data.setDetails.length <= 1) return;
    const nextDetails = data.setDetails.filter((_, sIdx) => sIdx !== index);
    const updatedDetails = nextDetails.map((set, sIdx) => ({
      ...set,
      setNumber: sIdx + 1,
    }));
    update({
      sets: data.sets - 1,
      setDetails: updatedDetails,
    });
  };

  const handleSetsChange = (raw) => {
    const sets = Number(raw);
    const setDetails = Array.from({ length: sets }, (_, index) => ({
      ...makeDefaultSet(index),
      ...(data.setDetails[index] || {}),
      setNumber: index + 1,
    }));
    update({ sets, setDetails });
  };

  const handleAttachmentUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push({ url: res.data.url, name: file.name, type: file.type });
      }
      update({ attachments: [...data.attachments, ...uploaded] });
      toast.success("Attachments uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload attachment");
    } finally {
      event.target.value = "";
    }
  };

  const removeAttachment = (url) => {
    update({
      attachments: data.attachments.filter((file) => file.url !== url),
    });
  };

  return (
    <div className="space-y-4">
      {showTitle && (
        <div className="space-y-2">
          <Label className="text-xs font-heading uppercase">{titleLabel}</Label>
          <Input
            value={data.title || ""}
            onChange={(event) => update({ title: event.target.value })}
            placeholder="Search existing workout or type a new one"
            disabled={disabled}
          />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-2">
          <Label className="text-xs font-heading uppercase">
            Workout Metric
          </Label>
          <Select
            value={data.workoutMetric}
            onValueChange={(workoutMetric) => update({ workoutMetric })}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WORKOUT_METRICS.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-heading uppercase">Difficulty</Label>
          <Select
            value={data.difficulty}
            onValueChange={(difficulty) => update({ difficulty })}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DIFFICULTIES.map((difficulty) => (
                <SelectItem key={difficulty.value} value={difficulty.value}>
                  {difficulty.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-heading uppercase">Sets</Label>
          <Select
            value={String(data.sets)}
            onValueChange={handleSetsChange}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 30 }, (_, index) => index + 1).map(
                (count) => (
                  <SelectItem key={count} value={String(count)}>
                    {count}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-heading uppercase">Set Details</Label>

        {data.setDetails.length > 0 && (
          <div className="hidden md:grid md:grid-cols-5 gap-2 p-2 border border-transparent text-[10px] font-heading uppercase text-muted-foreground font-semibold pb-0">
            <div></div> {/* Column 1: Set Number */}
            {data.workoutMetric === "reps" ? (
              <>
                <div>Reps</div> {/* Column 2 */}
                <div>Weight</div> {/* Column 3 */}
                <div>Rest</div> {/* Column 4 */}
                <div></div> {/* Column 5 */}
              </>
            ) : (
              <>
                <div>
                  {data.workoutMetric === "duration"
                    ? "Duration"
                    : data.workoutMetric === "distance"
                      ? "Distance"
                      : "Count"}
                </div>{" "}
                {/* Column 2 */}
                <div>Rest</div> {/* Column 3 */}
                <div></div> {/* Column 4 */}
                <div></div> {/* Column 5 */}
              </>
            )}
          </div>
        )}

        <div className="space-y-2">
          {data.setDetails.map((set, index) => (
            <div
              key={index}
              className="grid grid-cols-2 md:grid-cols-5 gap-2 rounded-lg border border-border bg-secondary/20 p-2"
            >
              <div className="flex items-center text-xs font-mono text-muted-foreground">
                Set {index + 1}
              </div>
              {data.workoutMetric === "reps" ? (
                <>
                  <Select
                    value={String(set.reps || "10")}
                    onValueChange={(reps) => updateSet(index, { reps })}
                    disabled={disabled}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Reps" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from(
                        { length: 50 },
                        (_, optionIndex) => optionIndex + 1,
                      ).map((count) => (
                        <SelectItem key={count} value={String(count)}>
                          {count} reps
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min="0"
                    value={set.weightKg || ""}
                    onChange={(event) =>
                      updateSet(index, { weightKg: event.target.value })
                    }
                    placeholder="Weight kg"
                    disabled={disabled}
                  />
                  <Select
                    value={String(set.restSeconds !== undefined && set.restSeconds !== null ? set.restSeconds : "none")}
                    onValueChange={(restSeconds) =>
                      updateSet(index, { restSeconds: restSeconds === "none" ? "none" : Number(restSeconds) })
                    }
                    disabled={disabled}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Rest" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {REST_OPTIONS.map((seconds) => (
                        <SelectItem key={seconds} value={String(seconds)}>
                          {formatRest(seconds)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center justify-end md:justify-center gap-1 col-span-2 md:col-span-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-secondary/40 rounded-sm"
                      onClick={() => duplicateSet(index)}
                      disabled={disabled}
                      title="Duplicate Set"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10 rounded-sm"
                      onClick={() => deleteSet(index)}
                      disabled={disabled || data.setDetails.length <= 1}
                      title="Delete Set"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {data.workoutMetric === "duration" && (
                    <Select
                      value={String(set.durationMin || "1")}
                      onValueChange={(durationMin) =>
                        updateSet(index, { durationMin })
                      }
                      disabled={disabled}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Duration" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from(
                          { length: 50 },
                          (_, optionIndex) => optionIndex + 1,
                        ).map((count) => (
                          <SelectItem key={count} value={String(count)}>
                            {count} min
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {data.workoutMetric === "distance" && (
                    <Select
                      value={String(set.distanceKm || "1")}
                      onValueChange={(distanceKm) =>
                        updateSet(index, { distanceKm })
                      }
                      disabled={disabled}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Distance" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from(
                          { length: 50 },
                          (_, optionIndex) => optionIndex + 1,
                        ).map((count) => (
                          <SelectItem key={count} value={String(count)}>
                            {count} km
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {data.workoutMetric === "count" && (
                    <Select
                      value={String(set.count || "10")}
                      onValueChange={(count) => updateSet(index, { count })}
                      disabled={disabled}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Count" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from(
                          { length: 100 },
                          (_, optionIndex) => optionIndex + 1,
                        ).map((count) => (
                          <SelectItem key={count} value={String(count)}>
                            {count} counts
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Select
                    value={String(set.restSeconds !== undefined && set.restSeconds !== null ? set.restSeconds : "none")}
                    onValueChange={(restSeconds) =>
                      updateSet(index, { restSeconds: restSeconds === "none" ? "none" : Number(restSeconds) })
                    }
                    disabled={disabled}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Rest" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {REST_OPTIONS.map((seconds) => (
                        <SelectItem key={seconds} value={String(seconds)}>
                          {formatRest(seconds)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center justify-end md:justify-center gap-1 col-span-2 md:col-span-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-secondary/40 rounded-sm"
                      onClick={() => duplicateSet(index)}
                      disabled={disabled}
                      title="Duplicate Set"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10 rounded-sm"
                      onClick={() => deleteSet(index)}
                      disabled={disabled || data.setDetails.length <= 1}
                      title="Delete Set"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <div className="hidden md:block"></div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-xs font-heading uppercase">
            Images / Video / PDF
          </Label>
          <div className="flex flex-wrap items-center gap-3">
            <input
              id={uploadId}
              type="file"
              multiple
              accept="image/*,video/*,application/pdf"
              className="hidden"
              onChange={handleAttachmentUpload}
              disabled={disabled}
            />
            <label htmlFor={uploadId} className="cursor-pointer">
              <div className="flex flex-col items-center justify-center w-14 h-14 border border-dashed border-border rounded-lg bg-secondary/10 hover:bg-secondary/20 transition-all gap-1 text-[10px] font-medium text-muted-foreground select-none">
                <Upload className="h-4 w-4" />
                <span>Upload</span>
              </div>
            </label>

            {data.attachments.map((file, idx) => {
              const fileIsImage = isImage(file);
              const fileUrl = file.url.startsWith("http")
                ? file.url
                : `${BACKEND_URL}${file.url}`;
              return (
                <div
                  key={file.url || idx}
                  className="relative group w-14 h-14 border border-border rounded-lg bg-secondary/20 flex items-center justify-center cursor-pointer overflow-hidden transition-all hover:border-primary/50"
                  onClick={() => {
                    if (fileIsImage) {
                      setPreviewDialog({
                        open: true,
                        src: fileUrl,
                        name: file.name || "Image Attachment",
                      });
                    } else {
                      window.open(fileUrl, "_blank");
                    }
                  }}
                >
                  {fileIsImage ? (
                    <img
                      src={fileUrl}
                      alt={file.name || "Attachment"}
                      className="w-full h-full object-cover transition-all group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center p-1 text-center">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                      <span className="text-[8px] text-muted-foreground truncate w-12 mt-0.5">
                        {file.name || "File"}
                      </span>
                    </div>
                  )}

                  {!disabled && (
                    <button
                      type="button"
                      className="absolute top-1 right-0 rounded-full bg-destructive text-destructive-foreground p-0.5 shadow hover:bg-destructive/90 transition-colors opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeAttachment(file.url);
                      }}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-heading uppercase">YouTube Link</Label>
          <Input
            value={data.youtubeLink || ""}
            onChange={(event) => update({ youtubeLink: event.target.value })}
            placeholder="https://youtube.com/..."
            disabled={disabled}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-xs font-heading uppercase">
            Workout / Safety Instructions
          </Label>
          <Textarea
            value={data.instructions || ""}
            onChange={(event) => update({ instructions: event.target.value })}
            placeholder="Instructions, cues, safety notes"
            rows={1}
            disabled={disabled}
            className="min-h-[30px]"
          />
        </div>
      </div>

      <Dialog
        open={previewDialog.open}
        onOpenChange={(o) => setPreviewDialog((d) => ({ ...d, open: o }))}
      >
        <DialogContent className="bg-card border-border max-w-sm p-4">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">
              {previewDialog.name}
            </DialogTitle>
          </DialogHeader>
          <img
            src={previewDialog.src}
            alt={previewDialog.name}
            className="rounded-sm max-h-80 w-full object-contain border border-border mt-2"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
